import {
  isInvidiousPlaylistId,
  type Playlist,
  type PlaylistEntry,
  type PlaylistSummary,
} from '../model/playlist.ts';
import { err, ok, parseError, type ParsedList, type ParseError, type Result } from '../result.ts';
import type { ParseContext } from './context.ts';
import { parseImageList, parseImageUrl } from './image.ts';
import { asArray, asBoolean, asIntLike, asNonEmptyString, asNumber, asObject, asString, field } from './raw.ts';
import { parseDescriptionNodes } from './richText.ts';
import { asEpochSeconds } from './time.ts';
import { parseVideoSummary } from './video.ts';

/**
 * R4 — one entry of `playlists[].videos`. The entry *is* a `VideoSummary` with two extra fields,
 * so it reuses that parser rather than restating it: whatever `parseVideoSummary` learns about the
 * abbreviated record (published-as-ISO, missing `viewCount`, `liveNow` over `type`) applies here
 * unchanged.
 *
 * `position` is the entry's index within the array as a **fallback only**. The API's own `index` is
 * authoritative and is what a paged response uses to keep numbering continuous across pages; the
 * fallback exists so a record missing the field still renders in order instead of collapsing to 0.
 */
export function parsePlaylistEntry(
  raw: unknown,
  ctx: ParseContext,
  position: number,
): Result<PlaylistEntry, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('PlaylistEntry', null, 'wrong-type'));

  const video = parseVideoSummary(raw, ctx);
  if (!video.ok) return err(video.error);

  return ok({
    // Absent on YouTube playlists, present on Invidious-owned ones. Never invented: a fabricated
    // key would 404 on removal, and §6 types it nullable precisely so it can be absent.
    indexId: asNonEmptyString(field(source, 'indexId')),
    index: asIntLike(field(source, 'index')) ?? position,
    video: video.value,
  });
}

function parsePlaylistEntryList(raw: unknown, ctx: ParseContext): ParsedList<PlaylistEntry> {
  const array = asArray(raw);
  if (!array) return { items: [], dropped: [] };
  const items: PlaylistEntry[] = [];
  const dropped: ParseError[] = [];
  array.forEach((entry, position) => {
    const parsed = parsePlaylistEntry(entry, ctx, position);
    if (parsed.ok) items.push(parsed.value);
    else dropped.push(parsed.error);
  });
  return { items, dropped };
}

/**
 * R4 — the `201` from `POST /api/v1/auth/playlists/{id}/videos`, which is **the whole entry row**:
 *
 * ```json
 * { "type":"video", "title":"Me at the zoo", "videoId":"jNQXAC9IVRw", …,
 *   "index":1, "indexId":"7096D5A5DFF0531A", "lengthSeconds":19, "liveNow":false }
 * ```
 *
 * So an add knows where the row landed and how to remove it again without a follow-up read. `position`
 * is 0 rather than the caller's guess because the row carries its own `index` — the fallback in
 * {@link parsePlaylistEntry} exists for a record that has lost the field, and inventing a position
 * here would be inventing the one thing this response is authoritative about.
 */
export function parseAddedPlaylistEntry(raw: unknown, ctx: ParseContext): Result<PlaylistEntry, ParseError> {
  return parsePlaylistEntry(raw, ctx, 0);
}

/**
 * R4 — the `201` from `POST /api/v1/auth/playlists`, which carries exactly two fields:
 * `{"title":"…","playlistId":"IVPL…"}`.
 *
 * `playlistId` is read from the **body** rather than from the `Location` header the documentation names,
 * and that is forced rather than preferred: the header is sent, and the instance's
 * `Access-Control-Expose-Headers` does not list it, so it is invisible to a browser
 * (`CREATED_PLAYLIST_LOCATION_IS_UNREADABLE`).
 *
 * `title` is echoed back **as stored**, which is worth keeping: it is where the 150-byte truncation
 * becomes visible, so a caller can compare and say so instead of leaving the reader to notice.
 */
export interface CreatedPlaylist {
  readonly playlistId: string;
  readonly title: string;
}

export function parseCreatedPlaylist(raw: unknown): Result<CreatedPlaylist, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('CreatedPlaylist', null, 'wrong-type'));

  const playlistId = asNonEmptyString(field(source, 'playlistId'));
  if (playlistId === null) return err(parseError('CreatedPlaylist', 'playlistId', 'missing'));

  return ok({ playlistId, title: asString(field(source, 'title')) ?? '' });
}

/**
 * R4 — `GET /api/v1/auth/playlists`: a bare array of **whole `Playlist` records**, entries embedded.
 *
 * There is no summary form and no envelope, so this reuses `parsePlaylist` per row rather than defining
 * a lighter shape — which also means the embedded entries are parsed and available, and
 * `playlistMembership` can read them (`playlist/manage.ts` has the cost measurement).
 *
 * A non-array is not fatal: `GET /api/v1/auth/playlists/` with an **empty id** falls through to this
 * route and answers `200` with the array, while the same path built from a real id answers an object.
 * A caller that mixes the two up gets an empty list rather than a throw.
 */
export function parseOwnedPlaylists(raw: unknown, ctx: ParseContext): ParsedList<Playlist> {
  const array = asArray(raw);
  if (!array) return { items: [], dropped: [] };
  const items: Playlist[] = [];
  const dropped: ParseError[] = [];
  for (const entry of array) {
    const parsed = parsePlaylist(entry, ctx);
    if (parsed.ok) items.push(parsed.value);
    else dropped.push(parsed.error);
  }
  return { items, dropped };
}

/**
 * R4 — the listing form, as it appears in `/api/v1/search`. A search playlist result carries an
 * **empty `videos: []`** (verified live and in the corpus), so this deliberately does not read
 * entries: offering an always-empty list would invite a caller to render "0 videos" over a
 * 35-video playlist.
 */
export function parsePlaylistSummary(raw: unknown, ctx: ParseContext): Result<PlaylistSummary, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('PlaylistSummary', null, 'wrong-type'));

  const playlistId = asNonEmptyString(field(source, 'playlistId'));
  if (playlistId === null) return err(parseError('PlaylistSummary', 'playlistId', 'missing'));

  return ok({
    playlistId,
    title: asString(field(source, 'title')) ?? '',
    author: asString(field(source, 'author')) ?? '',
    authorId: asNonEmptyString(field(source, 'authorId')),
    authorUrl: asNonEmptyString(field(source, 'authorUrl')),
    authorVerified: asBoolean(field(source, 'authorVerified')) ?? false,
    videoCount: asNumber(field(source, 'videoCount')),
    thumbnail: parseImageUrl(field(source, 'playlistThumbnail'), ctx),
    invidiousOwned: isInvidiousPlaylistId(playlistId),
  });
}

/**
 * R4 — `GET /api/v1/playlists/{id}`, both shapes: `type: "playlist"` (a mirrored YouTube playlist)
 * and `type: "invidiousPlaylist"` (one of ours). The two differ in exactly one structural way —
 * `indexId` on the entries — and that difference is carried per entry, not asserted from `type`,
 * which §2 warns is not a discriminator.
 *
 * An Invidious-native playlist also omits `authorId`/`authorUrl` and sends no `playlistThumbnail`,
 * so those are `null` rather than missing-and-fatal.
 */
export function parsePlaylist(raw: unknown, ctx: ParseContext): Result<Playlist, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('Playlist', null, 'wrong-type'));

  const playlistId = asNonEmptyString(field(source, 'playlistId'));
  if (playlistId === null) return err(parseError('Playlist', 'playlistId', 'missing'));

  const entries = parsePlaylistEntryList(field(source, 'videos'), ctx);
  const description = asString(field(source, 'description')) ?? '';

  return ok({
    playlistId,
    title: asString(field(source, 'title')) ?? '',
    author: asString(field(source, 'author')) ?? '',
    authorId: asNonEmptyString(field(source, 'authorId')),
    authorUrl: asNonEmptyString(field(source, 'authorUrl')),
    authorThumbnails: parseImageList(field(source, 'authorThumbnails'), ctx),
    description,
    descriptionNodes: parseDescriptionNodes(description, asString(field(source, 'descriptionHtml')) ?? ''),
    videoCount: asNumber(field(source, 'videoCount')),
    viewCount: asNumber(field(source, 'viewCount')),
    updatedEpochSec: asEpochSeconds(field(source, 'updated')),
    isListed: asBoolean(field(source, 'isListed')) ?? true,
    thumbnail: parseImageUrl(field(source, 'playlistThumbnail'), ctx),
    invidiousOwned: isInvidiousPlaylistId(playlistId),
    entries: entries.items,
    dropped: entries.dropped,
  });
}
